import { Suspense, lazy } from "react";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";

// Tiny helper wrapper: imported normally so the whole app is not waiting on an extra request.
import ScrollToTop from "./helpers/ScrollToTop";

// Every page stays lazy-loaded (each page becomes its own JS chunk)
const NSMSticky = lazy(() => import("./NSMSticky"));
const Home = lazy(() => import("./pages/Home"));
const Service = lazy(() => import("./pages/Service"));
const SingleService = lazy(() => import("./pages/SingleService"));
const SingleBlog = lazy(() => import("./pages/SingleBlog"));
const Blog = lazy(() => import("./pages/Blog"));
const Gallery = lazy(() => import("./pages/Gallery"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Sitemap = lazy(() => import("./components/Sitemap"));
const FAQs = lazy(() => import("./pages/FAQs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SaudiaArabia = lazy(() => import("./pages/SaudiaArabia"));
const Bahrain = lazy(() => import("./pages/Bahrain"));
const Kuwait = lazy(() => import("./pages/Kuwait"));
const Oman = lazy(() => import("./pages/Oman"));
const europe = lazy(() => import("./pages/europe"));
const Canada = lazy(() => import("./pages/Canada"));
const qatar = lazy(() => import("./pages/qatar"));
const uk = lazy(() => import("./pages/Uk"));
const USA = lazy(() => import("./pages/USA"));
const Ireland = lazy(() => import("./pages/Ireland"));
const Malaysia = lazy(() => import("./pages/Malaysia"));
const australia = lazy(() => import("./pages/australia"));
const India = lazy(() => import("./pages/India"));
const spain = lazy(() => import("./pages/spain"));
const newzealand = lazy(() => import("./pages/NewZealand"));
const CourierUK = lazy(() => import("./pages/CourierUK"));
const CourierCanada = lazy(() => import("./pages/CourierCanada"));
const CourierUSA = lazy(() => import("./pages/CourierUSA"));
const CourierEurope = lazy(() => import("./pages/CourierEurope"));
const Dubai = lazy(() => import("./pages/Dubai"));
const UAE = lazy(() => import("./pages/UAE"));
const AbuDhabi = lazy(() => import("./pages/AbuDhabi"));

// Shown only while a page chunk is downloading
const PageLoader = () => (
  <div className="spinner-wrapper">
    <div className="spinner">
      <div className="double-bounce1"></div>
      <div className="double-bounce2"></div>
    </div>
  </div>
);

/*
 * Removed on purpose:
 *  1) The fake 1-second "initialLoad" spinner. It stopped the Home page from even
 *     being requested until 1 second after load, which delayed the hero image paint.
 *  2) The requestIdleCallback that pre-imported Service, Blog and About. On the home
 *     page that silently downloaded about 2 MB of JavaScript (the Blog chunk contains
 *     all blog data) that most visitors never use.
 */
const App = () => {
  return (
    <Router>
      <ScrollToTop>
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route exact path="/" component={Home} />
            <Route path="/about" component={About} />
            <Route exact path="/services" component={Service} />
            <Route path="/services/:serviceTitle" component={SingleService} />
            <Route path="/services/:serviceTitle/:subMenu2" exact component={SingleService} />
            <Route path="/blog/:blogTitle" component={SingleBlog} />
            <Route path="/blog" component={Blog} />
            <Route path="/relocation-to-saudia-arabia" component={SaudiaArabia} />
            <Route path="/relocation-to-bahrain" component={Bahrain} />
            <Route path="/relocation-to-kuwait" component={Kuwait} />
            <Route path="/relocation-to-oman" component={Oman} />
            <Route path="/relocation-to-europe" component={europe} />
            <Route path="/relocation-to-canada" component={Canada} />
            <Route path="/relocation-to-qatar" component={qatar} />
            <Route path="/relocation-to-india" component={India} />
            <Route path="/relocation-to-uk" component={uk} />
            <Route path="/relocation-to-usa" component={USA} />
            <Route path="/relocation-to-ireland" component={Ireland} />
            <Route path="/relocation-to-malaysia" component={Malaysia} />
            <Route path="/relocation-to-australia" component={australia} />
            <Route path="/relocation-to-spain" component={spain} />
            <Route path="/relocation-to-newzealand" component={newzealand} />
            <Route path="/courier-service-to-uk" component={CourierUK} />
            <Route path="/courier-service-to-canada" component={CourierCanada} />
            <Route path="/courier-service-to-usa" component={CourierUSA} />
            <Route path="/courier-service-to-europe" component={CourierEurope} />
            <Route path="/movers-in-dubai" component={Dubai} />
            <Route path="/movers-in-uae" component={UAE} />
            <Route path="/movers-in-abudhabi" component={AbuDhabi} />
            <Route path="/gallery" component={Gallery} />
            <Route path="/contact" component={Contact} />
            <Route path="/sitemap" component={Sitemap} />
            <Route path="/faqs" component={FAQs} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>

        {/* Own Suspense boundary so the sticky buttons never hold up the page itself */}
        <Suspense fallback={null}>
          <NSMSticky />
        </Suspense>
      </ScrollToTop>
    </Router>
  );
};

export default App;